import { PROGRAM_MAPPING, IMPACT_SCORES } from './constants.js';
import { hasGlobalSouthCountry, hasGlobalSouthRegion } from '../countryMapping.js';

// Helper: check if a filter value is an active array (non-empty)
const isActiveArray = (v) => Array.isArray(v) && v.length > 0;
// Helper: check if a filter value is an active string (non-empty, not 'all', not an array)
const isActiveString = (v) => v && v !== 'all' && !Array.isArray(v);

// TOC alignment value → tocMappingType matching
const TOC_ALIGN_MATCH = {
  'toc': (t) => t === 'aligned-planned' || t === 'aligned-unplanned',
  'nontoc': (t) => t !== 'aligned-planned' && t !== 'aligned-unplanned',
  'linked': (t) => t === 'linked',
  'other': (t) => t === 'other',
};

// Check if a result matches a single program code
function matchesProgram(result, prog) {
  if (result.initiative === prog) return true;
  if (result.primarySubmitter?.includes(prog)) return true;
  if (result.contributingInitiatives?.some(init =>
    init === prog || init.includes(prog) || PROGRAM_MAPPING[init]?.includes(prog)
  )) return true;
  return false;
}

// Filter results by various criteria
// Supports both string values (Sidebar) and array values (Custom section multi-select)
export function filterResults(results, filters) {
  // Hoist array-based filter sets before the loop to avoid per-result allocation
  const fundingTypeSet = isActiveArray(filters.fundingType) ? new Set(filters.fundingType) : null;
  const statusSet = isActiveArray(filters.status) ? new Set(filters.status) : null;
  const reportingPhaseSet = isActiveArray(filters.reportingPhase) ? new Set(filters.reportingPhase) : null;
  const leadCenterSet = isActiveArray(filters.leadCenter) ? new Set(filters.leadCenter) : null;
  const centerInvolvementSet = isActiveArray(filters.centerInvolvement) ? new Set(filters.centerInvolvement) : null;
  const programArr = isActiveArray(filters.program) ? filters.program : null;
  const levelSet = isActiveArray(filters.level) ? new Set(filters.level) : null;
  const typeSet = isActiveArray(filters.type) ? new Set(filters.type) : null;
  const countryArr = isActiveArray(filters.country) ? filters.country.map(c => c.toLowerCase()) : null;
  const regionArr = isActiveArray(filters.region) ? filters.region.map(r => r.toLowerCase()) : null;
  const impactAreaArr = isActiveArray(filters.impactArea) ? filters.impactArea : null;
  const tocAlignArr = isActiveArray(filters.tocAlignment) ? filters.tocAlignment : null;
  const resultCodeSet = isActiveArray(filters.resultCode) ? new Set(filters.resultCode) : null;
  const aowSet = isActiveArray(filters.aow) ? new Set(filters.aow) : null;

  return results.filter(result => {
    // Filter by result code (exact match)
    if (resultCodeSet && !resultCodeSet.has(result.resultCode)) return false;
    // Filter by source (maps "w1w2" to W1W2, "w3bilat" to W3Bilateral)
    if (isActiveString(filters.source)) {
      if (filters.source === 'w1w2' && result.fundingType !== 'W1W2') return false;
      if (filters.source === 'w3bilat' && result.fundingType !== 'W3Bilateral') return false;
    }

    // Filter by program/initiative (string or array), respecting resultScope
    if (programArr) {
      if (!programArr.some(prog => matchesProgram(result, prog))) return false;
    } else if (isActiveString(filters.program)) {
      const prog = filters.program;
      const scope = filters.resultScope || 'all';
      if (scope === 'all') {
        if (!matchesProgram(result, prog)) return false;
      } else {
        const isPrimary = result.initiative === prog || (result.primarySubmitter?.includes(prog) ?? false);
        const isContrib = result.contributingInitiatives?.some(init =>
          init === prog || init.includes(prog) || PROGRAM_MAPPING[init]?.includes(prog)
        ) ?? false;
        if (scope === 'submitted' && !isPrimary) return false;
        if (scope === 'contributed' && (!isContrib || isPrimary)) return false;
      }
    }

    // Filter by year
    if (isActiveString(filters.year)) {
      if (result.year !== parseInt(filters.year)) return false;
    }

    // Filter by result level (string or array)
    if (levelSet) {
      if (!levelSet.has(result.level)) return false;
    } else if (isActiveString(filters.level)) {
      if (result.level !== filters.level) return false;
    }

    // Filter by result type (string or array)
    if (typeSet) {
      if (!typeSet.has(result.type)) return false;
    } else if (isActiveString(filters.type)) {
      if (result.type !== filters.type) return false;
    }

    // Filter by impact area (string or array)
    if (impactAreaArr) {
      const scoreMap = {
        'gender': result.genderLevel,
        'climate': result.climateLevel,
        'nutrition': result.nutritionLevel,
        'biodiversity': result.biodiversityLevel,
        'poverty': result.povertyLevel,
      };
      const meetsAny = impactAreaArr.some(area => {
        const score = IMPACT_SCORES[scoreMap[area]];
        return !filters.minScore || score >= filters.minScore;
      });
      if (!meetsAny) return false;
    } else if (isActiveString(filters.impactArea)) {
      const scoreMap = {
        'gender': result.genderLevel,
        'climate': result.climateLevel,
        'nutrition': result.nutritionLevel,
        'biodiversity': result.biodiversityLevel,
        'poverty': result.povertyLevel,
      };
      const score = IMPACT_SCORES[scoreMap[filters.impactArea]];
      if (filters.minScore && score < filters.minScore) return false;
    }

    // Filter by country and/or region — OR'd when both active (data has gaps in either field)
    const hasCountryFilter = countryArr || isActiveString(filters.country);
    const hasRegionFilter = regionArr || isActiveString(filters.region);
    if (hasCountryFilter && hasRegionFilter) {
      const rc = result.countries?.toLowerCase();
      const rr = result.regions?.toLowerCase();
      const matchesCountry = countryArr
        ? (rc && countryArr.some(c => rc.includes(c)))
        : (rc && rc.includes(filters.country.toLowerCase()));
      const matchesRegion = regionArr
        ? (rr && regionArr.some(r => rr.includes(r)))
        : (rr && rr.includes(filters.region.toLowerCase()));
      if (!matchesCountry && !matchesRegion) return false;
    } else if (hasCountryFilter) {
      const rc = result.countries?.toLowerCase();
      if (countryArr) {
        if (!rc || !countryArr.some(c => rc.includes(c))) return false;
      } else {
        if (!rc || !rc.includes(filters.country.toLowerCase())) return false;
      }
    } else if (hasRegionFilter) {
      const rr = result.regions?.toLowerCase();
      if (regionArr) {
        if (!rr || !regionArr.some(r => rr.includes(r))) return false;
      } else {
        if (!rr || !rr.includes(filters.region.toLowerCase())) return false;
      }
    }

    // Filter by geographic focus (Global South)
    if (filters.geographicFocus === 'globalSouth') {
      const countries = result.countries?.toLowerCase() || '';
      const hasCountryData = countries && countries !== 'not applicable';
      if (hasCountryData) {
        if (!hasGlobalSouthCountry(countries)) return false;
      } else {
        // No country data — fall back to region; exclude if only non-Global-South regions
        const regions = result.regions?.toLowerCase() || '';
        if (regions && regions !== 'not applicable' && !hasGlobalSouthRegion(regions)) return false;
      }
    }

    // Filter by funding type (W1W2 vs W3Bilateral) — array-based
    if (fundingTypeSet && !fundingTypeSet.has(result.fundingType)) return false;

    // Filter by TOC alignment (string or array)
    if (tocAlignArr) {
      if (!tocAlignArr.some(val => TOC_ALIGN_MATCH[val]?.(result.tocMappingType))) return false;
    } else if (isActiveString(filters.tocAlignment)) {
      const matcher = TOC_ALIGN_MATCH[filters.tocAlignment];
      if (matcher && !matcher(result.tocMappingType)) return false;
    }

    // Filter by Area of Work (uses tocHits from pipeline)
    if (aowSet) {
      const hits = result.tocHits;
      if (!hits) return false;
      const matchesAow = Object.values(hits).some(progHits =>
        Object.keys(progHits).some(aowName => aowSet.has(aowName))
      );
      if (!matchesAow) return false;
    }

    // Filter by Status (V22 — Quality Assessed, Editing, Approved, etc.)
    if (statusSet && !statusSet.has(result.status)) return false;

    // Filter by Reporting Phase (V22)
    if (reportingPhaseSet && !reportingPhaseSet.has(result.reportingPhase)) return false;

    // Filter by Lead Center
    if (leadCenterSet && !leadCenterSet.has(result.leadCenter)) return false;

    // Filter by Center Involvement (lead OR contributing)
    if (centerInvolvementSet && (!result.centerCodes || !result.centerCodes.some(c => centerInvolvementSet.has(c)))) return false;

    return true;
  });
}
